import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

/** API data point */
export interface SocKwApiPoint {
  ts: string;
  kw: number;
  soc: number;
}

/** Chart formatted point */
interface ChartPoint {
  time: string;
  power: number;
  soc: number;
}

/** Component Props */
interface ChargingSpeedSocChartProperties {
  data: SocKwApiPoint[];
  available?: boolean;
}

/** Label renderer props */
interface ReferenceLabelProperties {
  // eslint-disable-next-line react/require-default-props
  viewBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  value: string;
  // eslint-disable-next-line react/require-default-props
  variant?: 'start' | 'stop';
}

/**
 * Formats ISO timestamp → HH:mm
 * @param {string} iso - The ISO date string
 * @returns {string} The formatted time string
 */
const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Custom Tooltip
 * @param {TooltipProps<ValueType, NameType>} properties - The tooltip properties
 * @returns {JSX.Element | null} The tooltip component
 */
function CustomTooltip({ active, payload }: TooltipProps<ValueType, NameType>): JSX.Element | null {
  if (!active || !payload?.length) return null;
  const { t } = useTranslation('endConsumer');

  const { time, power, soc } = payload[0].payload as ChartPoint;

  return (
    <div className="charging-tooltip">
      <p className="time">{time}</p>
      <p className="power">
        {t('chargingSpeed')}: {(power ?? 0).toFixed(1)} kW
      </p>
      <p className="soc">
        {t('soc')}: {(soc ?? 0).toFixed(1)} %
      </p>
    </div>
  );
}

/**
 * Custom Reference Label
 * @param {ReferenceLabelProperties} properties - The reference label properties
 * @returns {JSX.Element | null} The rendered SVG reference label
 */
function ReferenceLabel({
  viewBox,
  value,
  variant = 'start',
}: ReferenceLabelProperties): JSX.Element | null {
  if (!viewBox) return null;

  const { x, y, width } = viewBox;
  const labelWidth = 64;
  const labelHeight = 28;
  const paddingRight = 0;
  const bgColor = variant === 'start' ? '#22C55E' : '#EF4444';

  return (
    <g transform={`translate(${x + width - labelWidth - paddingRight}, ${y - labelHeight / 2})`}>
      <rect width={labelWidth} height={labelHeight} rx={14} ry={14} fill={bgColor} />
      <text
        x={labelWidth / 2}
        y={labelHeight / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#ffffff"
        fontSize={12}
        fontWeight={600}>
        {value}
      </text>
    </g>
  );
}

/**
 * Charging Speed vs SOC Chart
 * @param {ChargingSpeedSocChartProperties} properties - The component properties
 * @returns {JSX.Element} The rendered chart component
 */
function ChargingSpeedSocChart({ data, available }: ChargingSpeedSocChartProperties): JSX.Element {
  const { t } = useTranslation('endConsumer');
  /**
   * Transform API data → chart format
   */
  const chartData: ChartPoint[] = useMemo(
    () =>
      data.map((item) => ({
        time: formatTime(item.ts),
        power: item.kw,
        soc: item.soc,
      })),
    [data],
  );

  if (!chartData.length) {
    return (
      <div className="no-charging-data">
        <p>{t('noChargingDataAvailable')}</p>
      </div>
    );
  }

  const startPower = chartData[0].power;
  const stopPower = chartData[chartData.length - 1].power;
  const stopTime = chartData[chartData.length - 1].time;

  return (
    <div className="charging-chart-wrapper">
      <div className="heading">
        <p className="title">{t('socChargingSpeedGraph')}</p>
        <div className="sub-title">
          <p className="title-kw">
            <span className="indicator" /> kW
          </p>
          <p className="title-soc">
            <span className="indicator" />
            SOC %
          </p>
        </div>
      </div>

      <div className="content">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <XAxis dataKey="time" tickLine={false} axisLine={false} />

            <YAxis
              yAxisId="left"
              domain={[0, 'auto']}
              tickFormatter={(value: number) => `${value} kW`}
              tickLine={false}
              axisLine={false}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y}
                  fill="var(--primary-color, #2E9E3A)"
                  fontSize={12}
                  textAnchor="end"
                  dominantBaseline="middle">
                  {payload.value} kW
                </text>
              )}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 100]}
              tickFormatter={(value: number) => `${value}%`}
              tickLine={false}
              axisLine={false}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y}
                  fill="var(--secondary-color, #0099D6)"
                  fontSize={12}
                  textAnchor="start"
                  dominantBaseline="middle">
                  {payload.value}%
                </text>
              )}
            />

            <Tooltip content={<CustomTooltip />} />

            <ReferenceLine
              x={stopTime}
              yAxisId="left"
              stroke="var(--primary-color, #00B3A4)"
              strokeDasharray="4 4"
            />

            {/* CENTER DOT */}
            <ReferenceDot
              x={stopTime}
              y={stopPower}
              yAxisId="left"
              r={12}
              fill="var(--card-bg-gradient-color, #E6FFFA)"
              stroke="var(--primary-color, #00B3A4)"
              strokeWidth={2}
              label={{ value: '⚡', position: 'center', fontSize: 14 }}
            />

            {/* POWER LINE */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="power"
              stroke="var(--primary-color, #2E9E3A)"
              strokeWidth={3}
              dot={false}
            />

            {/* SOC LINE */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="soc"
              stroke="var(--secondary-color, #0088FE)"
              strokeWidth={3}
              dot={false}
            />

            {!available && (
              <>
                <ReferenceLine
                  y={startPower}
                  yAxisId="left"
                  stroke="#22C55E"
                  strokeDasharray="3 3"
                  label={<ReferenceLabel value={t('start')} variant="start" />}
                />
                <ReferenceLine
                  y={stopPower}
                  yAxisId="left"
                  stroke="#EF4444"
                  strokeDasharray="3 3"
                  label={<ReferenceLabel value={t('stop')} variant="stop" />}
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

ChargingSpeedSocChart.defaultProps = {
  available: false,
};

export default ChargingSpeedSocChart;
